/**
 * Simulation Redux Slice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  OrderLifecycleResponse,
  SimulationStepId,
  ActivityEntry,
  SimulationFactory,
  SimulationZone,
  SimulationCamera,
  SimulationQrReader,
  ScanMode,
  ScanStepKey,
} from '../domain/types/simulation.types'

export interface SimulationState {
  orderNumber: string | null
  lifecycle: OrderLifecycleResponse | null
  loadingLifecycle: boolean
  lifecycleError: string | null
  executingStep: SimulationStepId | null
  stepError: string | null
  stepErrorDetails: Record<string, unknown> | null
  activityLog: Record<SimulationStepId, ActivityEntry[]>

  // Factory context
  selectedFactory: SimulationFactory | null
  loadingFactoryContext: boolean
  factoryContextError: string | null

  // Zones
  entryZone: SimulationZone | null
  weighbridgeZone: SimulationZone | null
  exitZone: SimulationZone | null

  // Devices per zone
  entryReaders: SimulationQrReader[]
  entryCameras: SimulationCamera[]
  weighbridgeReaders: SimulationQrReader[]
  weighbridgeCameras: SimulationCamera[]
  exitReaders: SimulationQrReader[]
  exitCameras: SimulationCamera[]

  // Scan mode per step
  scanMode: Record<ScanStepKey, ScanMode>

  // Last scan response (to display to user)
  lastScanResponse: Record<string, unknown> | null
  lastScanStep: SimulationStepId | null
}

const emptyActivityLog: Record<SimulationStepId, ActivityEntry[]> = {
  order_created: [],
  assign_resource: [],
  join_queue: [],
  entry_scan: [],
  weighbridge_scan: [],
  first_weigh: [],
  second_weigh: [],
  exit_scan: [],
  invoices: [],
}

const initialState: SimulationState = {
  orderNumber: null,
  lifecycle: null,
  loadingLifecycle: false,
  lifecycleError: null,
  executingStep: null,
  stepError: null,
  stepErrorDetails: null,
  activityLog: { ...emptyActivityLog },

  selectedFactory: null,
  loadingFactoryContext: false,
  factoryContextError: null,

  entryZone: null,
  weighbridgeZone: null,
  exitZone: null,

  entryReaders: [],
  entryCameras: [],
  weighbridgeReaders: [],
  weighbridgeCameras: [],
  exitReaders: [],
  exitCameras: [],

  scanMode: {
    entry_scan: 'qr',
    weighbridge_scan: 'qr',
    exit_scan: 'qr',
  },

  lastScanResponse: null,
  lastScanStep: null,
}

export const simulationSlice = createSlice({
  name: 'shipments/simulation',
  initialState,
  reducers: {
    loadLifecycle: (state, action: PayloadAction<string>) => {
      state.orderNumber = action.payload
      state.loadingLifecycle = true
      state.lifecycleError = null
    },
    loadLifecycleSuccess: (state, action: PayloadAction<OrderLifecycleResponse>) => {
      state.lifecycle = action.payload
      state.loadingLifecycle = false
      state.lifecycleError = null
    },
    loadLifecycleFailure: (state, action: PayloadAction<string>) => {
      state.loadingLifecycle = false
      state.lifecycleError = action.payload
    },

    executeStep: (state, action: PayloadAction<{ stepId: SimulationStepId; params: Record<string, unknown> }>) => {
      state.executingStep = action.payload.stepId
      state.stepError = null
      state.stepErrorDetails = null
    },
    executeStepSuccess: (state, action: PayloadAction<SimulationStepId>) => {
      state.executingStep = null
      state.stepError = null
      state.stepErrorDetails = null
      void action.payload
    },
    executeStepFailure: (state, action: PayloadAction<{ stepId: SimulationStepId; error: string; details?: Record<string, unknown> | null }>) => {
      state.executingStep = null
      state.stepError = action.payload.error
      state.stepErrorDetails = action.payload.details ?? null
    },

    addActivityEntry: (state, action: PayloadAction<{ stepId: SimulationStepId; entry: ActivityEntry }>) => {
      state.activityLog[action.payload.stepId].push(action.payload.entry)
    },
    setActivityLog: (state, action: PayloadAction<Record<SimulationStepId, ActivityEntry[]>>) => {
      state.activityLog = action.payload
    },

    // Factory context actions
    selectFactory: (state, action: PayloadAction<SimulationFactory>) => {
      state.selectedFactory = action.payload
      state.factoryContextError = null
      // Clear previously loaded zone data
      state.entryZone = null
      state.weighbridgeZone = null
      state.exitZone = null
      state.entryReaders = []
      state.entryCameras = []
      state.weighbridgeReaders = []
      state.weighbridgeCameras = []
      state.exitReaders = []
      state.exitCameras = []
    },
    loadFactoryContext: (state, _action: PayloadAction<string>) => {
      state.loadingFactoryContext = true
      state.factoryContextError = null
    },
    loadFactoryContextSuccess: (
      state,
      action: PayloadAction<{
        entryZone: SimulationZone | null
        weighbridgeZone: SimulationZone | null
        exitZone: SimulationZone | null
        entryReaders: SimulationQrReader[]
        entryCameras: SimulationCamera[]
        weighbridgeReaders: SimulationQrReader[]
        weighbridgeCameras: SimulationCamera[]
        exitReaders: SimulationQrReader[]
        exitCameras: SimulationCamera[]
      }>
    ) => {
      state.loadingFactoryContext = false
      const p = action.payload
      state.entryZone = p.entryZone
      state.weighbridgeZone = p.weighbridgeZone
      state.exitZone = p.exitZone
      state.entryReaders = p.entryReaders
      state.entryCameras = p.entryCameras
      state.weighbridgeReaders = p.weighbridgeReaders
      state.weighbridgeCameras = p.weighbridgeCameras
      state.exitReaders = p.exitReaders
      state.exitCameras = p.exitCameras
    },
    loadFactoryContextFailure: (state, action: PayloadAction<string>) => {
      state.loadingFactoryContext = false
      state.factoryContextError = action.payload
    },

    setScanMode: (state, action: PayloadAction<{ step: ScanStepKey; mode: ScanMode }>) => {
      state.scanMode[action.payload.step] = action.payload.mode
    },

    setLastScanResponse: (
      state,
      action: PayloadAction<{ step: SimulationStepId; response: Record<string, unknown> }>
    ) => {
      state.lastScanResponse = action.payload.response
      state.lastScanStep = action.payload.step
    },

    reset: () => initialState,
  },
})

export const simulationActions = simulationSlice.actions
export const simulationReducer = simulationSlice.reducer
